/**
 * Sticky Add to Cart Button
 * Creates a sticky version of the add to cart button that appears when scrolling
 */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.originalButton = null;
    this.stickyContainer = null;
    this.stickyButton = null;
    this.isVisible = false;
    this.observer = null;
    this.buttonClassObserver = null;
    this.bundleObserver = null;
    this.mediaObserver = null;
    this.productForm = null;
    this._bundleTimer = null;
    this._mediaTimer = null;
    this._listeners = [];
    this._subscriptions = [];
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Find the primary product form's ATC button (scoped — not cross-sell or
    // recommended-products forms). Fall back through wider selectors.
    this.originalButton = this.resolveOriginalButton();
    if (!this.originalButton) return;

    // Find the product form (primary)
    this.productForm = this.originalButton.closest('product-form') ||
                       document.querySelector('main product-form') ||
                       document.querySelector('product-form');
    if (!this.productForm) return;

    // Create sticky container
    this.createStickyContainer();

    // Setup intersection observer
    this.setupObserver();

    // Watch for the target being replaced (variant re-render)
    this.setupTargetMutationFallback();

    // Listen for variant changes
    this.setupVariantListener();

    // Listen for cart updates
    this.setupCartListener();

    // Listen for bundle updates
    this.setupBundleListener();

    // Compensate body padding when bar height reflows
    this.setupResizeObserver();
  }

  resolveOriginalButton() {
    return document.querySelector('product-form .product-form__submit') ||
           document.querySelector('main .product-form__submit') ||
           document.querySelector('.product-form__submit');
  }

  createStickyContainer() {
    // Create the sticky container element
    this.stickyContainer = document.createElement('div');
    this.stickyContainer.className = 'sticky-add-to-cart';
    this.stickyContainer.setAttribute('aria-hidden', 'true');
    this.stickyContainer.setAttribute('role', 'region');
    this.stickyContainer.setAttribute('aria-label', 'Add to cart');

    // Create inner wrapper
    const innerWrapper = document.createElement('div');
    innerWrapper.className = 'sticky-add-to-cart__inner page-width';

    // Create product info section
    const productInfo = document.createElement('div');
    productInfo.className = 'sticky-add-to-cart__info';

    // Add product image
    const productImage = this.getCurrentProductImage();
    if (productImage) {
      const img = document.createElement('img');
      img.src = productImage.src;
      img.alt = productImage.alt;
      img.className = 'sticky-add-to-cart__image';
      img.loading = 'lazy';
      productInfo.appendChild(img);
    }

    // Add product title and price
    const titleElement = document.querySelector('.product__title h1');
    const priceElement = document.querySelector('.price__regular .price-item--regular') ||
                        document.querySelector('.price__sale .price-item--sale');

    if (titleElement) {
      const title = document.createElement('h3');
      title.className = 'sticky-add-to-cart__title';
      title.textContent = titleElement.textContent.trim();
      productInfo.appendChild(title);
    }

    // Create price container that can handle bundle pricing
    const priceContainer = document.createElement('div');
    priceContainer.className = 'sticky-add-to-cart__price-container';

    if (priceElement) {
      const price = document.createElement('span');
      price.className = 'sticky-add-to-cart__price';
      price.setAttribute('aria-live', 'polite');
      price.textContent = priceElement.textContent;
      priceContainer.appendChild(price);
    }

    productInfo.appendChild(priceContainer);

    // Create button container
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'sticky-add-to-cart__button-container';

    // Clone the original button
    this.stickyButton = this.originalButton.cloneNode(true);
    this.stickyButton.removeAttribute('id');
    this.stickyButton.className = 'sticky-add-to-cart__button product-form__submit button button--full-width';
    this.stickyButton.setAttribute('aria-live', 'polite');

    // Store original text for later use
    this.stickyButton.setAttribute('data-add-to-cart-text', this.originalButton.textContent.trim());

    // Handle button click — supports soft-disabled scroll-to-picker for
    // SELECT SIZE state per UX spec §6.3 pattern B.
    this.stickyButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.stickyButton.getAttribute('aria-disabled') === 'true') {
        this.scrollToVariantPicker();
        return;
      }
      window._wrAtcSource = 'sticky';
      this.handleAddToCart();
    });

    // Mark primary ATC clicks so add_to_cart events can attribute to 'primary'
    if (this.originalButton && !this.originalButton._wrAtcSourceHooked) {
      this.originalButton.addEventListener('click', () => {
        window._wrAtcSource = 'primary';
      }, { capture: true });
      this.originalButton._wrAtcSourceHooked = true;
    }

    buttonContainer.appendChild(this.stickyButton);

    // Assemble the sticky container
    innerWrapper.appendChild(productInfo);
    innerWrapper.appendChild(buttonContainer);
    this.stickyContainer.appendChild(innerWrapper);

    // Append to body
    document.body.appendChild(this.stickyContainer);
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: '0px 0px 0px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // In-page ATC visible — hide sticky.
          this.hide();
        } else if (entry.boundingClientRect.top < 0) {
          // In-page ATC has scrolled above the viewport — show sticky.
          this.show();
        } else {
          // In-page ATC is still below the fold (initial state pre-scroll
          // or short page where target sits below). Keep sticky hidden.
          this.hide();
        }
      });
    }, options);

    this.observer.observe(this.originalButton);
  }

  setupTargetMutationFallback() {
    const root = this.productForm.parentElement || document.querySelector('main') || document.body;
    if (!root) return;
    this.targetMutationObserver = new MutationObserver(() => {
      if (this.originalButton && document.contains(this.originalButton)) return;
      const fresh = this.resolveOriginalButton();
      if (fresh && fresh !== this.originalButton) {
        if (this.observer) this.observer.disconnect();
        this.originalButton = fresh;
        this.setupObserver();
      }
    });
    this.targetMutationObserver.observe(root, {
      childList: true,
      subtree: true
    });
  }

  setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined' || !this.stickyContainer) return;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isVisible) this.applyBodyPadding();
    });
    this.resizeObserver.observe(this.stickyContainer);
  }

  _addDocListener(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this._listeners.push({ target, event, handler, options });
  }

  setupVariantListener() {
    this._addDocListener(document, 'variant:change', (event) => {
      this.updateStickyButton(event.detail);
    }, { passive: true });

    if (window.subscribe && window.PUB_SUB_EVENTS) {
      this._subscriptions.push(
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, (variant) => {
          this.updateStickyButton(variant);
        })
      );
    }

    // Also listen for custom variant picker changes
    const variantRadios = document.querySelectorAll('input[name="id"]');
    variantRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        window.requestAnimationFrame(() => {
          this.syncButtonStates();
          this.updateStickyImage();
        });
      }, { passive: true });
    });

    // Watch for media changes
    this.watchMediaChanges();
  }

  setupCartListener() {
    this._addDocListener(document, 'cart:updated', () => {
      this.syncButtonStates();
    }, { passive: true });

    if (window.subscribe && window.PUB_SUB_EVENTS) {
      this._subscriptions.push(
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
          this.syncButtonStates();
        })
      );
    }

    if (this.productForm) {
      this._addDocListener(this.productForm, 'product-form:state-change', () => {
        this.syncButtonStates();
      }, { passive: true });
    }

    // Watch for class changes on the original button (loading state).
    // The product form's .finally() may remove loading after cartUpdate fires,
    // so this observer catches the actual class removal.
    if (this.originalButton) {
      this.buttonClassObserver = new MutationObserver(() => {
        this.syncButtonStates();
      });
      this.buttonClassObserver.observe(this.originalButton, { attributes: true, attributeFilter: ['class'] });
    }
  }

  setupBundleListener() {
    if (!window.bundlePricing) return;

    this._addDocListener(document, 'bundle:updated', () => {
      window.requestAnimationFrame(() => {
        this.syncButtonStates();
        this.updateBundlePrice();
      });
    }, { passive: true });

    // Debounced fallback observer for bundle content changes
    const debouncedUpdate = () => {
      if (this._bundleTimer) clearTimeout(this._bundleTimer);
      this._bundleTimer = setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.syncButtonStates();
          this.updateBundlePrice();
        });
      }, 100);
    };

    if (this.originalButton) {
      this.bundleObserver = new MutationObserver(debouncedUpdate);
      this.bundleObserver.observe(this.originalButton, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  updateBundlePrice() {
    if (!window.bundlePricing || !window.bundlePricing.currentBundle) return;

    const priceContainer = this.stickyContainer.querySelector('.sticky-add-to-cart__price-container');
    if (!priceContainer) return;

    const bundle = window.bundlePricing.currentBundle;
    const total = bundle.discountedPrice * bundle.quantity;
    const retailTotal = bundle.basePrice * bundle.quantity;
    const hasDiscount = total < retailTotal;

    const formatPrice = (amount) => {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(amount * 100, window.Shopify.money_format || '${{amount}}');
      }
      return '$' + amount.toFixed(2);
    };

    let priceHTML = '';
    if (hasDiscount) {
      priceHTML = `
        <span class="sticky-add-to-cart__price sticky-add-to-cart__price--compare">
          <s>${formatPrice(retailTotal)}</s>
        </span>
        <span class="sticky-add-to-cart__price sticky-add-to-cart__price--sale">
          ${formatPrice(total)}
        </span>
      `;
    } else {
      priceHTML = `
        <span class="sticky-add-to-cart__price">
          ${formatPrice(total)}
        </span>
      `;
    }

    priceContainer.innerHTML = priceHTML;
  }

  updateStickyButton(variant) {
    if (!this.stickyButton) return;

    const priceElement = this.stickyContainer.querySelector('.sticky-add-to-cart__price');
    const stickyImage = this.stickyContainer.querySelector('.sticky-add-to-cart__image');

    if (variant) {
      if (variant.available) {
        this.stickyButton.disabled = false;
        this.stickyButton.textContent = this.stickyButton.getAttribute('data-add-to-cart-text') || 'Add to cart';
      } else {
        this.stickyButton.disabled = true;
        this.stickyButton.textContent = 'Sold out';
      }

      if (priceElement && variant.price && typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        priceElement.textContent = Shopify.formatMoney(variant.price, window.Shopify?.money_format || '${{amount}}');
      }

      if (stickyImage && variant.featured_image) {
        stickyImage.src = variant.featured_image.src;
        stickyImage.alt = variant.featured_image.alt || variant.title;
      }
    }

    this.syncButtonStates();
  }

  watchMediaChanges() {
    const mediaWrapper = document.querySelector('.product__media-wrapper');
    if (!mediaWrapper) return;

    const debouncedImageUpdate = () => {
      if (this._mediaTimer) clearTimeout(this._mediaTimer);
      this._mediaTimer = setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.updateStickyImage();
        });
      }, 150);
    };

    this.mediaObserver = new MutationObserver(debouncedImageUpdate);
    this.mediaObserver.observe(mediaWrapper, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }

  getCurrentProductImage() {
    return document.querySelector('.thumbnail-list__item.is-active img, .thumbnail-list__item[aria-current="true"] img') ||
           document.querySelector('.product__media.is-active img, .product__modal-opener--image.is-active img, .product__media-item.is-active img') ||
           document.querySelector('.product__media img:not(.skip-lazy)') ||
           null;
  }

  updateStickyImage() {
    const stickyImage = this.stickyContainer.querySelector('.sticky-add-to-cart__image');
    if (!stickyImage) return;

    const currentImage = this.getCurrentProductImage();
    if (currentImage && currentImage.src) {
      stickyImage.src = currentImage.src;
      stickyImage.alt = currentImage.alt || '';
    }
  }

  syncButtonStates() {
    if (this.stickyButton && this.originalButton) {
      this.stickyButton.disabled = this.originalButton.disabled;

      // Soft-disabled aria state when no size variant is selected.
      const allSizeRadios = this.productForm?.querySelectorAll('.size-option input[type="radio"], fieldset.product-form__input input[type="radio"][name^="Size"]') || [];
      const noSizeChecked = allSizeRadios.length > 0 &&
        ![...allSizeRadios].some(r => r.checked);
      if (noSizeChecked) {
        this.stickyButton.setAttribute('aria-disabled', 'true');
      } else {
        this.stickyButton.removeAttribute('aria-disabled');
      }

      // Check if this is a bundle product with custom pricing display
      const isBundleProduct = window.bundlePricing && this.originalButton.innerHTML.includes('text-decoration: line-through');

      if (isBundleProduct) {
        this.stickyButton.innerHTML = this.originalButton.innerHTML;
      } else {
        this.stickyButton.textContent = this.originalButton.textContent;
      }

      // Copy loading state
      if (this.originalButton.classList.contains('loading')) {
        this.stickyButton.classList.add('loading');
        this.stickyButton.setAttribute('aria-busy', 'true');
      } else {
        this.stickyButton.classList.remove('loading');
        this.stickyButton.removeAttribute('aria-busy');
      }
    }
  }

  handleAddToCart() {
    if (this.productForm) {
      this.originalButton.click();
      this.syncButtonStates();
    }
  }

  scrollToVariantPicker() {
    const picker = document.querySelector('.product-form__input--pill[name^="Size"]') ||
                   document.querySelector('.size-option') ||
                   document.querySelector('.enhanced-variant-picker');
    if (!picker) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({
      behavior: reduced ? 'auto' : 'smooth',
      block: 'center'
    });
    window.requestAnimationFrame(() => {
      const firstUncheckedRadio = picker.querySelector('input[type="radio"]:not(:checked)');
      if (firstUncheckedRadio && typeof firstUncheckedRadio.focus === 'function') {
        firstUncheckedRadio.focus({ preventScroll: true });
      }
    });
  }

  show() {
    if (!this.isVisible) {
      this.stickyContainer.classList.add('is-visible');
      this.stickyContainer.setAttribute('aria-hidden', 'false');
      this.isVisible = true;
      this.applyBodyPadding();

      this.syncButtonStates();
      this.updateStickyImage();

      if (window.bundlePricing) {
        this.updateBundlePrice();
      }
    }
  }

  hide() {
    if (this.isVisible) {
      this.stickyContainer.classList.remove('is-visible');
      this.stickyContainer.setAttribute('aria-hidden', 'true');
      this.isVisible = false;
      this.removeBodyPadding();
    }
  }

  applyBodyPadding() {
    if (!this.stickyContainer || window.matchMedia('(min-width: 750px)').matches) return;
    const h = this.stickyContainer.getBoundingClientRect().height;
    if (h > 0) document.body.style.paddingBottom = h + 'px';
  }

  removeBodyPadding() {
    document.body.style.paddingBottom = '';
  }

  disconnectedCallback() {
    // Disconnect all observers
    if (this.observer) this.observer.disconnect();
    if (this.buttonClassObserver) this.buttonClassObserver.disconnect();
    if (this.bundleObserver) this.bundleObserver.disconnect();
    if (this.mediaObserver) this.mediaObserver.disconnect();
    if (this.targetMutationObserver) this.targetMutationObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();

    // Clear debounce timers
    if (this._bundleTimer) clearTimeout(this._bundleTimer);
    if (this._mediaTimer) clearTimeout(this._mediaTimer);

    // Remove document-level event listeners
    this._listeners.forEach(({ target, event, handler, options }) => {
      target.removeEventListener(event, handler, options);
    });
    this._listeners = [];

    // Unsubscribe PubSub subscriptions
    this._subscriptions.forEach(unsub => {
      if (typeof unsub === 'function') unsub();
    });
    this._subscriptions = [];

    this.removeBodyPadding();

    if (this.stickyContainer) {
      this.stickyContainer.remove();
    }
  }
}

// Define the custom element
customElements.define('sticky-add-to-cart', StickyAddToCart);

// Initialize on DOM content loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyAddToCart);
} else {
  initStickyAddToCart();
}

function initStickyAddToCart() {
  const isProductPage = document.querySelector('.product-form__submit');
  const isCheckout = window.location.pathname.includes('/checkouts/');
  const isCart = window.location.pathname.includes('/cart');

  if (isProductPage && !isCheckout && !isCart) {
    const stickyElement = document.createElement('sticky-add-to-cart');
    document.body.appendChild(stickyElement);
  }
}
